/**
 * Machine-readable failures raised by the shared SVG map scene.
 *
 * Presentation features should branch on these stable codes rather than parsing diagnostics when
 * mapping renderer failures to their own loading, retry or error UI.
 */
export type MapSceneErrorCode =
  | 'INVALID_CONFIGURATION'
  | 'INVALID_MODEL'
  | 'NOT_RENDERED'
  | 'ENTITY_NOT_FOUND'
  | 'COORDINATE_TRANSFORM_UNAVAILABLE'
  | 'DESTROYED'
  | 'RENDER_FAILED';

/**
 * Typed failure raised when the scene cannot honour its public contract.
 *
 * The stable {@link code} lets page-level callers map a technical failure to their
 * own loading or error UI without parsing an English diagnostic message.
 */
export class MapSceneError extends Error {
  /** Stable category used by page-level error handling. */
  public readonly code: MapSceneErrorCode;
  /** Original runtime failure retained when wrapping SVG or browser API errors. */
  public readonly cause?: unknown;

  /**
   * Creates a typed renderer failure.
   *
   * @param params Failure category, developer-facing diagnostic and optional wrapped cause.
   * @returns A `MapSceneError` instance carrying the stable failure code.
   */
  public constructor(params: { code: MapSceneErrorCode; message: string; cause?: unknown }) {
    super(params.message);
    this.name = 'MapSceneError';
    this.code = params.code;
    this.cause = params.cause;
  }
}
